import React from 'react';
import { Container } from 'semantic-ui-react';
import { UploadToDelete } from '@api/upload/dto/UploadToDelete';
import { hasPermissionsDeleteUpload } from '@utils/UserRoleSelector';
import useGetUser from '@api/user/hooks/useGetUser';
import { UserDTO } from '@api/user/dtos/UserDTO';
import UploadStatus, { StatusName, UploadStatusType } from '@constants/uploadStatus';
import DownloadableFile from '@components/CustomCell/DownloadableFile';
import StatusIcon from '@components/CustomCell/StatusIcon';
import DetailsButton from '@components/CustomCell/DetailsButton';
import DeleteOption from '@components/DeleteRow/DeleteOption';
import ValidationMessage from '@components/CustomCell/ValidationMessage';

export type ColumnName =
  | 'OPTIONS'
  | 'FILE_NAME'
  | 'UPLOAD_VALID'
  | 'UPLOAD_SENT'
  | 'UPLOAD_ACCEPTED'
  | 'UPLOADED_BY_FIRST_NAME'
  | 'VALIDATION_MESSAGE';
type CustomComponent = (...args: any) => React.ReactNode;
type RefreshFunction = (refresh: boolean) => void;

function renderDelete(status: UploadStatusType, uploadCompany: string, user: UserDTO) {
  return (
    (UploadStatus.NOT_VALIDATED === status || UploadStatus.CANCELLED === status) &&
    hasPermissionsDeleteUpload(user, uploadCompany)
  );
}

const customCellsDefinition: { [columnName in ColumnName]: CustomComponent } = {
  FILE_NAME: (rowValues: any) => {
    const { ID, FILE_NAME } = rowValues;
    return <DownloadableFile fileName={FILE_NAME as string} uploadId={ID as number} />;
  },
  UPLOAD_VALID: (rowValues: any) => {
    const { UPLOAD_VALID } = rowValues;
    return <StatusIcon statusValue={UploadStatus[UPLOAD_VALID as StatusName]?.isValid} />;
  },
  UPLOAD_SENT: (rowValues: any) => {
    const { UPLOAD_SENT } = rowValues;
    return <StatusIcon statusValue={UploadStatus[UPLOAD_SENT as StatusName]?.isSent} />;
  },
  UPLOAD_ACCEPTED: (rowValues: any) => {
    const { UPLOAD_ACCEPTED } = rowValues;
    return <StatusIcon statusValue={UploadStatus[UPLOAD_ACCEPTED as StatusName]?.isAccepted} />;
  },
  UPLOADED_BY_FIRST_NAME: (rowValues: any) => {
    const { UPLOADED_BY_FIRST_NAME, UPLOADED_BY_LAST_NAME } = rowValues;
    return `${UPLOADED_BY_FIRST_NAME} ${UPLOADED_BY_LAST_NAME}`;
  },
  VALIDATION_MESSAGE: (rowValues: any) => {
    const { VALIDATION_MESSAGE_JSON, VALIDATION_MESSAGE } = rowValues;
    return (
      <ValidationMessage
        validationMessageOld={VALIDATION_MESSAGE}
        validationMessageJson={VALIDATION_MESSAGE_JSON}
      />
    );
  },
  OPTIONS: (rowValues: any, setRefresh: RefreshFunction) => {
    const { data: user } = useGetUser();
    const { ID, FILE_NAME, STATUS_BACKGROUND, COMPANY_BACKGROUND } = rowValues;
    const uploadToDelete: UploadToDelete = {
      id: ID as number,
      name: FILE_NAME as string,
    };
    if (!user) {
      return null;
    }
    return (
      <Container
        onClick={(e: Event) => {
          e.stopPropagation();
        }}
      >
        <DetailsButton entityType="files" entityId={ID} />
        <DeleteOption
          uploadToDelete={uploadToDelete}
          triggerUploadsTableRefresh={setRefresh}
          rendered={renderDelete(
            UploadStatus[STATUS_BACKGROUND as StatusName],
            COMPANY_BACKGROUND as string,
            user
          )}
        />
      </Container>
    );
  },
};

export default customCellsDefinition;
